import React, { useState } from "react";
import NavBar from "@/components/navigation/NavBar";
import config from "@/config/config";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import { Card } from "@/components/ui/card";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import SuccessModal from "@/components/Motion/SuccessModal";

// Define the validation schemas per step.
// Update your schemas:
// Replace the existing stepSchemas definition with this:
const stepSchemas = {
  1: z.object({
    rs_types: z
      .array(z.string())
      .nonempty({ message: "Bitte wählen Sie mindestens einen Immobilientyp." }),
    transactionTypes: z
      .array(z.string())
      .nonempty({ message: "Bitte wählen Sie einen Transaktionstyp." }),
  }),
  // SIMPLIFIED Step 2 Schema: Removed .refine()
  2: z.object({
    cities: z.string().nonempty({ message: "Bitte geben Sie Städte oder PLZ an." }), // Changed message slightly
    minPriceRent: z.string().optional(),
    maxPriceRent: z.string().optional(),
    minPrice: z.string().optional(),
    maxPrice: z.string().optional(),
    // We need transactionTypes available here for the check later,
    // but it's defined in step 1's formData. We'll access it directly.
  }),
  3: z.object({
    // Optional features
    balcony: z.boolean().optional(), // Mark as optional if they can be unchecked
    terrace: z.boolean().optional(),
    garden: z.boolean().optional(),
    elevator: z.boolean().optional(),
    parking: z.boolean().optional(),
    emailNotifications: z.boolean().optional(), // This seems to be the only required field based on renderStep
  }),
  4: z.object({
    first_name: z.string().nonempty({ message: "Vorname ist erforderlich." }),
    last_name: z.string().nonempty({ message: "Nachname ist erforderlich." }),
    email: z.string().email({ message: "Bitte geben Sie eine gültige E-Mail-Adresse ein." }),
    home_phone: z.string().nonempty({ message: "Telefonnummer ist erforderlich." }),
    accept_contact: z.literal(true, {
      errorMap: () => ({ message: "Sie müssen die Datenschutzbestimmungen akzeptieren." }),
    }),
    // newsletter and description are optional based on renderStep
    newsletter: z.boolean().optional(),
    description: z.string().optional(),
  }),
};

export default function SuchprofilForm() {
  const totalSteps = 4;
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extend initial state with missing fields for prices:
  const [formData, setFormData] = useState({
    // Step 1: Property Types & Transaction Types
    rs_types: [],
    transactionTypes: [],
    // Step 2: Location & Price
    regions: [],
    cities: "",
    minPriceRent: "",
    maxPriceRent: "",
    minPrice: "",
    maxPrice: "",
    // Step 3: Property Specifications
    minRooms: "",
    maxRooms: "",
    minLivingSpace: "",
    maxLivingSpace: "",
    // Step 4: Additional Features & Condition
    balcony: false,
    terrace: false,
    garden: false,
    elevator: false,
    parking: false,
    condition: "",
    energyEfficiencyClass: "",
    // Step 5: Notification Preferences
    emailNotifications: true,
    pushNotifications: false,
    notificationFrequency: "daily",
  });

  const [contactData, setContactData] = useState({
    first_name: "",
    last_name: "",
    street: "",
    zip_code: "",
    city: "",
    country: "",
    email: "",
    home_phone: "",
    accept_contact: false,
    newsletter: false,
    description: "",
    merkmal:""
  });

  // Handle changes for the suchprofil fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (
      type === "checkbox" &&
      (name === "rs_types" || name === "transactionTypes" || name === "regions")
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: checked
          ? [...(prev[name] || []), value]
          : (prev[name] || []).filter((item) => item !== value),
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle changes for the contact fields (step 4)
  const handleContactInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Validation function for the current step.
// Replace the existing validateStep function with this:
const validateStep = () => {
  const currentSchema = stepSchemas[step];
  if (!currentSchema) return { success: true }; // Should not happen

  // Use contactData for step 4, formData otherwise
  const dataToValidate = step === 4 ? contactData : formData;
  const baseResult = currentSchema.safeParse(dataToValidate);

  // If base validation fails, return immediately
  if (!baseResult.success) {
    return baseResult;
  }

  // --- Additional Step 2 Logic for Prices ---
  if (step === 2) {
    const isRent = formData.transactionTypes?.[0] === "RENT"; // Check selected transaction type

    if (isRent) {
      // Check if both rent prices are entered (and maybe > 0 if needed)
      if (!formData.minPriceRent?.trim() || !formData.maxPriceRent?.trim()) {
        // Return a custom error structure similar to Zod's
        return {
          success: false,
          error: {
            issues: [ // Use 'issues' like Zod for consistency if preferred
              { path: ["minPriceRent"], message: "Mindest- und Maximalmiete sind erforderlich." }
            ]
          }
        };
      }
      // Optional: Add check if max >= min
      // if (parseInt(formData.minPriceRent) > parseInt(formData.maxPriceRent)) { ... }

    } else { // Assumes BUY if not RENT
      // Check if both purchase prices are entered
      if (!formData.minPrice?.trim() || !formData.maxPrice?.trim()) {
        return {
          success: false,
          error: {
            issues: [
              { path: ["minPrice"], message: "Mindest- und Maximalpreis sind erforderlich." }
            ]
          }
        };
      }
       // Optional: Add check if max >= min
       // if (parseInt(formData.minPrice) > parseInt(formData.maxPrice)) { ... }
    }
  }
  // --- End Additional Step 2 Logic ---

  // If base validation succeeded and any additional checks passed
  return baseResult; // Which is { success: true, data: ... }
};
  // Navigation functions
// Replace the existing handleNext function with this:
const handleNext = () => {
  const result = validateStep();
  if (!result.success) {
    // Map errors to our error state. Assumes error structure has 'issues' array.
    const errorObj = {};
    // Use result.error.issues if available, otherwise result.error.errors (fallback)
    const issues = result.error.issues || result.error.errors || [];
    issues.forEach((err) => {
      // Ensure path exists and has at least one element
      if (err.path && err.path.length > 0) {
        errorObj[err.path[0]] = err.message;
      }
    });
    setErrors(errorObj);
    console.log("Validation Errors:", errorObj); // Log errors for debugging
  } else {
    setErrors({});
    if (step < totalSteps) { // Ensure we don't go beyond the last step
       setStep((prev) => prev + 1);
    }
  }
};
  const handleBack = () => {
    setErrors({});
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    // Validate final step (contactData)
    setLoading(true); // Set loading state
    const result = stepSchemas[4].safeParse(contactData);
    if (!result.success) {
      const errorObj = {};
      result.error.errors.forEach((err) => {
        errorObj[err.path[0]] = err.message;
      });
      setErrors(errorObj);
      setLoading(false); // Reset loading state
      return;
    }
    setErrors({});
    try {
      // 1️⃣ Check the user selected transaction type
      const marketing = formData.transactionTypes[0] || "RENT";
      if (marketing === "RENT") {
        contactData.merkmal = "Mietinteressent";
      } else if (marketing === "BUY") {
        contactData.merkmal = "Kaufinteressent";
      }
      // 1️⃣ Create Contact
      const contactResponse = await fetch(`${config.apiUrl}/api/create-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactData),
      });
      if (!contactResponse.ok) {
        const errorData = await contactResponse.json();
        throw new Error(errorData.error || "Fehler beim Erstellen des Kontakts");
      }
      const contactResult = await contactResponse.json();
      const newClientId = contactResult.client_id;

      // 2️⃣ Build the payload for the Suchprofil using the correct field names.
      const sanitizedData = {
        saved_query: {
          client_id: newClientId,
          shop_id: 120004,
          rs_types:
            Array.isArray(formData.rs_types) && formData.rs_types.length
              ? formData.rs_types
              : [formData.rs_types],
          base_rent:
            parseInt(formData.minPriceRent, 10) || 0,
          base_rent_to:
            parseInt(formData.maxPriceRent, 10) || 0,
          price: parseInt(formData.minPrice, 10) || 0,
          price_to: parseInt(formData.maxPrice, 10) || 0,
          marketing_type: formData.transactionTypes[0] || "RENT",
          cities: Array(formData.cities) || "",
          number_of_rooms: parseInt(formData.minRooms, 10) || 0,
          number_of_rooms_to: parseInt(formData.maxRooms, 10) || 0,
          living_space: parseInt(formData.minLivingSpace, 10) || 0,
          living_space_to: parseInt(formData.maxLivingSpace, 10) || 0,
          condition: formData.condition || "",
          balcony: Boolean(formData.balcony),
          garden: Boolean(formData.garden),
          parking: Boolean(formData.parking),
        },
      };

        console.log("Sanitized data to send:", sanitizedData);
      // 3️⃣ Create Suchprofil using the payload
      const suchprofilResponse = await fetch(`${config.apiUrl}/api/suchprofil`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sanitizedData),
      });
      if (!suchprofilResponse.ok) {
        const errorData = await suchprofilResponse.json();
        throw new Error(errorData.error || "Fehler beim Erstellen des Suchprofils");
      }
      const suchprofilResult = await suchprofilResponse.json();
      setShowModal(true); // Show success modal
    } catch (error) {
      console.error("Submission error:", error);
      alert(error.message);
      setLoading(false); // Reset loading state

    }finally{
      setLoading(false); // Reset loading state
    }
  };
  // Render a progress bar at the top
  const renderProgressBar = () => {
    const progressPercentage = Math.floor((step / totalSteps) * 100);
    return (
      <div className="w-full mb-6">
        <progress
          className="progress progress-primary w-full"
          value={progressPercentage}
          max="100"
        ></progress>
        <div className="text-center mt-1 text-xs text-prime-900">
          Schritt {step} von {totalSteps}
        </div>
      </div>
    );
  };

  const types = [
    { name: "Wohnung", type: "APARTMENT", icons: "/Icons/Abfrage/abfrage-immobilienart-wohnung.svg" },
    { name: "Haus", type: "HOUSE", icons: "/Icons/Abfrage/abfrage-immobilienart-haus.svg" },
    { name: "Grundstück", type: "TRADE_SITE", icons: "/Icons/Abfrage/abfrage-immobilienart-grundstuck.svg" },
    { name: "Anlageobjekt", type: "INVESTMENT", icons: "/Icons/Abfrage/abfrage-immobilienart-gewerbe.svg" },
  ];
  const marketingType = [
    { name: "Kaufen", type: "BUY" },
    { name: "Mieten", type: "RENT" },
  ];

  // Render the content for each step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="z-10">
            <p className="text-sm">Suchprofil</p>
            <h2 className="text-xl text-prime-950 font-semibold mb-6">
              Immobilientypen
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {types.map((item) => {
                const selected = formData.rs_types.includes(item.type);
                return (
                  <label
                    key={item.type}
                    className={`group rounded-xl card p-4 cursor-pointer text-center transition-all ${
                      selected
                        ? "border-none bg-prime-800 text-gray-200"
                        : "bg-prime-50 border-gray-200 text-prime-950"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="rs_types"
                      value={item.type}
                      checked={selected}
                      onChange={handleInputChange}
                      className="hidden"
                    />
                    {console.log(item.icons)}
                    <img
                      src={item.icons}
                      alt={item.name}
                      className="mx-auto w-12 h-16 object-contain"
                    />
                    <span className="mt-2 block text-sm font-semibold">
                      {item.name}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.rs_types && (
              <p className="text-red-500 text-xs mt-1">{errors.rs_types}</p>
            )}
            <div className="mt-6">
              <h3 className="font-semibold text-prime-950 mb-4">
                Transaktionstypen
              </h3>
              <div className="btn-group">
                {marketingType.map((type) => {
                  const selected = formData.transactionTypes.includes(type.type);
                  return (
                    <button
                      key={type.type}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          transactionTypes: [type.type],
                        }))
                      }
                      className={`btn ${
                        selected
                          ? "border-none bg-prime-800 text-gray-200"
                          : "btn-outline border-none  bg-prime-50"
                      }`}
                    >
                      {type.name}
                    </button>
                  );
                })}
              </div>
              {errors.transactionTypes && (
                <p className="text-red-500 text-xs mt-1">{errors.transactionTypes}</p>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <p className="text-sm">Suchprofil</p>
            <h2 className="text-xl text-prime-950 font-semibold mb-6">
              Standort & Preis
            </h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Städte (z.B. Eschweiler, Aachen)"
                name="cities"
                value={formData.cities}
                onChange={handleInputChange}
                className="input input-bordered input-md bg-gray-100 w-full"
              />
              {errors.cities && (
                <p className="text-red-500 text-xs mt-1">{errors.cities}</p>
              )}
              {formData.transactionTypes[0] === "RENT" ? (
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    placeholder="Mindestmiete (€)"
                    name="minPriceRent"
                    value={formData.minPriceRent}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  <input
                    type="number"
                    placeholder="Maximalmiete (€)"
                    name="maxPriceRent"
                    value={formData.maxPriceRent}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  {errors.minPriceRent && (
                    <p className="text-red-500 text-xs mt-1">{errors.minPriceRent}</p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    placeholder="Mindestpreis (€)"
                    name="minPrice"
                    value={formData.minPrice}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  <input
                    type="number"
                    placeholder="Maximalpreis (€)"
                    name="maxPrice"
                    value={formData.maxPrice}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  {errors.minPrice && (
                    <p className="text-red-500 text-xs mt-1">{errors.minPrice}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <p className="text-sm">Suchprofil</p>
            <h2 className="text-xl text-prime-950 font-semibold mb-6">
              Zusätzliche Features
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {[["balcony", "Balkon"], ["terrace", "Terasse"], ["garden", "Garten"], ["elevator", "Fahrstuhl"],["parking", "Stellplatz"]].map(
                (feature) => (
                  <label key={feature[0]} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name={feature[0]}
                      checked={formData[feature[0]]}
                      onChange={handleInputChange}
                      className="checkbox"
                    />
                    <span>
                      {feature[1]}
                    </span>
                  </label>
                )
              )}
            </div>
            <div>
              <h2 className="text-lg text-prime-950 font-semibold my-6">
                Benachrichtigungen
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={formData.emailNotifications}
                      onChange={handleInputChange}
                      className="checkbox"
                    />
                    <span>E-Mail-Benachrichtigungen</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <p className="text-sm">Suchprofil</p>
            <h2 className="text-xl text-prime-950 font-semibold mb-6">
              Kontakt erstellen
            </h2>
            <div className="space-y-4">
              <input
                type="text"
                name="first_name"
                placeholder="Vorname"
                value={contactData.first_name}
                onChange={handleContactInputChange}
                className="input input-bordered input-md bg-gray-100 w-full"
              />
              {errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
              <input
                type="text"
                name="last_name"
                placeholder="Nachname"
                value={contactData.last_name}
                onChange={handleContactInputChange}
                className="input input-bordered input-md bg-gray-100 w-full"
              />
              {errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
              <input
                type="email"
                name="email"
                placeholder="E-Mail"
                value={contactData.email}
                onChange={handleContactInputChange}
                className="input input-bordered input-md bg-gray-100 w-full"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
              <input
                type="text"
                name="home_phone"
                placeholder="Telefonnummer"
                value={contactData.home_phone}
                onChange={handleContactInputChange}
                className="input input-bordered input-md bg-gray-100 w-full"
              />
              {errors.home_phone && (
                <p className="text-red-500 text-xs mt-1">{errors.home_phone}</p>
              )}
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="accept_contact"
                  checked={contactData.accept_contact}
                  onChange={handleContactInputChange}
                  className="checkbox"
                />
                <span className="text-sm text-slate-600">
                  Ich akzeptiere die Datenschutzbestimmungen
                </span>
              </label>
              {errors.accept_contact && (
                <p className="text-red-500 text-xs mt-1">{errors.accept_contact}</p>
              )}
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="newsletter"
                  checked={contactData.newsletter}
                  onChange={handleContactInputChange}
                  className="checkbox"
                />
                <span className="text-sm text-slate-600">
                  Newsletter abonnieren
                </span>
              </label>
              <textarea
                name="description"
                placeholder="Nachricht (optional)"
                value={contactData.description}
                onChange={handleContactInputChange}
                className="textarea bg-gray-100 textarea-bordered w-full"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return ( 
    <div className="bg-prime-50 min-h-screen flex flex-col"> 
      <SuccessModal modal={showModal} title="Suchprofil angelegt!" />
      <NavBar className="flex-shrink-0" /> 
      <div className="max-w-6xl mx-auto mt-20 md:mt-24 relative px-4 w-full flex-grow"> 
        <img
          className="h-[40rem] translate-x-10 md:-translate-y-20 rounded-md object-cover"
          src="/Fotos/suchprofil-suchprofil-jakobs.webp"
          alt=""
        />
        <div className="absolute  left-0 top-0 w-full flex gap-3 justify-center items-end">
          <div className="flex-1">
            <Card className="shadow-none hidden sm:translate-x-10 z-0 border-none bg-prime-800 sm:flex flex-col p-10 translate-y-3">
              <h3 className="text-white pb-2 text-3xl font-serif">
              Neue Angebote direkt in dein Postfach
              </h3>
              <p className="text-prime-50">
              Viele unserer Immobilien gehen nur an vorgemerkte Suchkunden. Erstellen Sie jetzt Ihr Suchprofil und sichern Sie sich exklusive Angebote!
              </p>
            </Card>
          </div>
          <Card className="shadow-none rounded-md z-20 border-none flex flex-col min-h-[40rem] w-full max-w-[27rem]">
            <CardContent className="flex-1 flex flex-col"> 
              {renderProgressBar()}
              <div className="flex-grow"> 
                {renderStep()}
              </div>
            </CardContent>
            <CardActions className="flex justify-between self-end px-4 pb-4">
              {step > 1 && (
                <button
                  onClick={handleBack}
                  className="btn bg-white py-2 flex items-center border-none text-prime-800 hover:bg-prime-200"
                >
                  Zurück
                </button>
              )}
              {step < 4 ? (
                <button
                  onClick={handleNext}
                  className="btn border-none bg-prime-900 py-2 text-white"
                >
                  Weiter
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="btn bg-[#e95c0c] text-white border-none hover:bg-[#e95c0c]/80 focus:bg-[#e95c0c]"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    "Suchprofil erstellen"
                  )}
                </button>
              )}
            </CardActions>
          </Card>
        </div>
      </div>
    </div>
  );
}
